import { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { usePWA } from '../hooks/usePWA';
import { Wifi, WifiOff, Download, RefreshCw, X } from 'lucide-react';

/**
 * Offline Indicator Component
 * Shows offline status, install prompt, and update notifications
 */
export function OfflineIndicator() {
  const { t, isHindi } = useLanguage();
  const { isOnline, isInstallable, updateAvailable, installApp, applyUpdate } = usePWA();
  const [showOffline, setShowOffline] = useState(false);
  const [showInstall, setShowInstall] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);

  // Show offline indicator when going offline
  useEffect(() => {
    if (!isOnline) {
      setShowOffline(true);
    } else {
      // Show briefly then hide
      const timer = setTimeout(() => setShowOffline(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline]);

  // Show install prompt when available
  useEffect(() => {
    if (isInstallable) {
      setShowInstall(true);
    }
  }, [isInstallable]);

  // Show update notification
  useEffect(() => {
    if (updateAvailable) {
      setShowUpdate(true);
    }
  }, [updateAvailable]);

  const handleInstall = async () => {
    const installed = await installApp();
    if (installed) {
      setShowInstall(false);
    }
  };

  const handleUpdate = () => {
    applyUpdate();
    setShowUpdate(false);
  };

  return (
    <>
      {/* Offline Banner */}
      {showOffline && (
        <div 
          className={`fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ${
            isOnline ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          <div className="flex items-center justify-center gap-2 py-2 px-4 text-white text-sm">
            {isOnline ? (
              <>
                <Wifi className="w-4 h-4" />
                <span>{isHindi ? 'वापस ऑनलाइन' : 'Back online'}</span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4" />
                <span>{isHindi ? 'आप ऑफ़लाइन हैं' : 'You are offline'}</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Install Prompt */}
      {showInstall && (
        <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50">
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-lg p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-[var(--primary)]/10 rounded-lg">
                <Download className="w-5 h-5 text-[var(--primary)]" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-[var(--text-primary)] text-sm">
                  {isHindi ? 'ऐप इंस्टॉल करें' : 'Install App'}
                </h4>
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  {isHindi 
                    ? 'ऑफ़लाइन उपयोग के लिए आध्या इंस्टॉल करें'
                    : 'Install AADHYAA for offline access'}
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleInstall}
                    className="px-3 py-1.5 bg-[var(--primary)] text-white text-xs rounded-lg hover:bg-[var(--primary-dark)] transition-colors"
                  >
                    {isHindi ? 'इंस्टॉल करें' : 'Install'}
                  </button>
                  <button
                    onClick={() => setShowInstall(false)}
                    className="px-3 py-1.5 text-[var(--text-muted)] text-xs hover:text-[var(--text-primary)] transition-colors"
                  >
                    {isHindi ? 'बाद में' : 'Later'}
                  </button>
                </div>
              </div>
              <button 
                onClick={() => setShowInstall(false)}
                className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Available */}
      {showUpdate && (
        <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50">
          <div className="bg-[var(--surface)] border border-[var(--primary)] rounded-xl shadow-lg p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-[var(--primary)]/10 rounded-lg">
                <RefreshCw className="w-5 h-5 text-[var(--primary)]" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-[var(--text-primary)] text-sm">
                  {isHindi ? 'अपडेट उपलब्ध' : 'Update Available'}
                </h4>
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  {isHindi 
                    ? 'नई सुविधाओं के लिए अपडेट करें'
                    : 'Update for new features'}
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleUpdate}
                    className="px-3 py-1.5 bg-[var(--primary)] text-white text-xs rounded-lg hover:bg-[var(--primary-dark)] transition-colors"
                  >
                    {isHindi ? 'अपडेट करें' : 'Update Now'}
                  </button>
                  <button
                    onClick={() => setShowUpdate(false)}
                    className="px-3 py-1.5 text-[var(--text-muted)] text-xs hover:text-[var(--text-primary)] transition-colors"
                  >
                    {isHindi ? 'बाद में' : 'Later'}
                  </button>
                </div>
              </div>
              <button 
                onClick={() => setShowUpdate(false)}
                className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default OfflineIndicator;
